<script lang="ts">
import { onMounted, shallowRef } from 'vue';
import { errorString } from '@/utils/helpers/rest-response';
import axios from 'axios';
import { formatAnyDate } from '@/utils/helpers/dates';
import { formatDistance } from 'date-fns';
import { useSnackbarManager } from '@/modules/snackbar/services/snackbar-manager';
import logger from '@/modules/common/services/logger.service';
import { useInterval } from '@/composables/native';

interface ApiBlacklistRecord {
  ip: string;
  blockStartsAt: string;
  blockEndsAt: string;
}

interface BlacklistRecord {
  ip: string;
  blockEndsAt: Date;
}

const tableColumns = [
  { text: 'IP', value: 'ip', class: 'text-no-wrap text-truncate' },
  {
    text: 'Blocked Until',
    value: 'blockEndsAt',
    class: 'text-no-wrap text-truncate',
  },
  { value: 'actions', sortable: false, align: 'end', width: 30 },
];
</script>

<script setup lang="ts">
const snackbarManager = useSnackbarManager();

const blacklistRecords = shallowRef<BlacklistRecord[]>([]);
const now = shallowRef(new Date());

useInterval(() => {
  now.value = new Date();
}, 1000);

function formatDate(date: Date): string {
  return formatAnyDate(date, 'hh:mm:ss MM/dd/yy');
}

function formatRelativeTime(d: Date, baseDate: Date): string {
  return formatDistance(d, baseDate, { addSuffix: true, includeSeconds: true });
}

async function unbanIp(ip: string): Promise<void> {
  try {
    await axios.delete(`/api/1/broker-admin/ipblacklist/${ip}`);
    snackbarManager.confirm('IP has been unbanned');
  } catch (e) {
    snackbarManager.error(errorString(e));
  }

  void refreshIps();
}

async function refreshIps(): Promise<void> {
  try {
    blacklistRecords.value = await getBlacklistRecords();
  } catch (e) {
    logger.warn(e);
  }
}

async function getBlacklistRecords(): Promise<BlacklistRecord[]> {
  const { data } = await axios.get<{ ips: ApiBlacklistRecord[] }>(
    `/api/1/broker-admin/ipblacklist`
  );

  return data.ips && data.ips.length
    ? data.ips.map((record) => ({
        ip: record.ip,
        blockEndsAt: new Date(record.blockEndsAt),
      }))
    : [];
}

onMounted(async () => {
  await refreshIps();
});
</script>

<template>
  <div>
    <VDataTable
      class="d-flex flex flex-column col-6"
      dense
      disable-pagination
      fixed-header
      :headers="tableColumns"
      height="100%"
      hide-default-footer
      :items="blacklistRecords"
      :no-data-text="
        $tc('no IPs are currently blacklisted', blacklistRecords.length, {
          count: blacklistRecords.length,
        })
      "
      sort-by="blockEndsAt"
    >
      <template #[`item.ip`]="{ item }">
        <span>{{ item.ip }}</span>
      </template>
      <template #[`item.blockEndsAt`]="{ item }">
        <span>
          {{ formatDate(item.blockEndsAt) }} ({{ formatRelativeTime(item.blockEndsAt, now) }})
        </span>
      </template>

      <template #[`item.actions`]="{ item }">
        <div class="d-flex flex-row justify-end row-actions">
          <VBtn class="ma-1" icon small @click="unbanIp(item.ip)">
            <VIcon>mdi-delete</VIcon>
          </VBtn>
        </div>
      </template>
    </VDataTable>
  </div>
</template>

<style lang="scss" scoped></style>
